import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import {
  Shield, Activity, Bell, Send, Clock, User, AlertTriangle,
  CheckCircle2, RefreshCw, ChevronLeft, ChevronRight
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";

type AuditLog = {
  id: string; userId: string | null; userName: string; userEmail: string;
  companyId: string | null; action: string; resource: string;
  resourceId: string | null; details: any; ipAddress: string | null;
  userAgent: string | null; createdAt: string;
};

const ACTION_COLORS: Record<string, string> = {
  login: "bg-blue-50 text-blue-700",
  logout: "bg-gray-100 text-gray-600",
  create: "bg-emerald-50 text-emerald-700",
  update: "bg-amber-50 text-amber-700",
  delete: "bg-rose-50 text-rose-600",
  broadcast_announcement: "bg-violet-50 text-violet-700",
};

export default function CPSystemAdmin() {
  const [page, setPage] = useState(0);
  const [announcementTitle, setAnnouncementTitle] = useState("");
  const [announcementMessage, setAnnouncementMessage] = useState("");

  const { data: logsData, isLoading, refetch } = useQuery({
    queryKey: ["cp-audit-logs", page],
    queryFn: async () => {
      const res = await fetch(`/api/cp/audit-logs?limit=50&offset=${page * 50}`, { credentials: "include" });
      if (!res.ok) throw new Error("Failed to fetch audit logs");
      return res.json() as Promise<{ logs: AuditLog[]; total: number }>;
    },
  });

  const broadcast = useMutation({
    mutationFn: async () => {
      const res = await fetch("/api/cp/announcements/broadcast", {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ title: announcementTitle, message: announcementMessage, type: "announcement" }),
      });
      if (!res.ok) throw new Error("Failed to broadcast");
      return res.json();
    },
    onSuccess: (data: any) => {
      setAnnouncementTitle("");
      setAnnouncementMessage("");
      alert(`Announcement sent to ${data.sent} conversations`);
      refetch();
    },
  });

  const logs = logsData?.logs ?? [];
  const total = logsData?.total ?? 0;
  const totalPages = Math.ceil(total / 50);

  return (
    <div className="space-y-6 animate-in fade-in duration-300">
      <div>
        <h2 className="text-xl font-bold text-gray-900">System Administration</h2>
        <p className="text-sm text-gray-500 mt-1">Audit logs, announcements, and system monitoring</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="bg-white rounded-2xl p-6 border border-gray-200/80 shadow-sm flex items-center gap-4">
          <div className="w-12 h-12 rounded-xl bg-emerald-500 flex items-center justify-center">
            <CheckCircle2 className="w-6 h-6 text-white" />
          </div>
          <div>
            <div className="text-2xl font-bold text-gray-900">Healthy</div>
            <div className="text-xs text-gray-500">System Status</div>
          </div>
        </div>
        <div className="bg-white rounded-2xl p-6 border border-gray-200/80 shadow-sm flex items-center gap-4">
          <div className="w-12 h-12 rounded-xl bg-blue-500 flex items-center justify-center">
            <Activity className="w-6 h-6 text-white" />
          </div>
          <div>
            <div className="text-2xl font-bold text-gray-900">{total}</div>
            <div className="text-xs text-gray-500">Total Audit Events</div>
          </div>
        </div>
        <div className="bg-white rounded-2xl p-6 border border-gray-200/80 shadow-sm flex items-center gap-4">
          <div className="w-12 h-12 rounded-xl bg-violet-500 flex items-center justify-center">
            <Shield className="w-6 h-6 text-white" />
          </div>
          <div>
            <div className="text-2xl font-bold text-gray-900">Active</div>
            <div className="text-xs text-gray-500">Security Monitoring</div>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-2xl p-6 border border-gray-200/80 shadow-sm">
        <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <Bell className="w-4 h-4 text-violet-500" /> Broadcast Announcement
        </h3>
        <div className="space-y-3">
          <Input
            placeholder="Announcement title (optional)"
            value={announcementTitle}
            onChange={e => setAnnouncementTitle(e.target.value)}
            className="rounded-xl"
          />
          <Textarea
            placeholder="Write your announcement message..."
            value={announcementMessage}
            onChange={e => setAnnouncementMessage(e.target.value)}
            rows={3}
            className="rounded-xl resize-none"
          />
          <Button
            onClick={() => broadcast.mutate()}
            disabled={!announcementMessage.trim() || broadcast.isPending}
            className="bg-violet-600 hover:bg-violet-700 text-white rounded-xl gap-2"
          >
            {broadcast.isPending ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            {broadcast.isPending ? "Sending..." : "Broadcast to All Users"}
          </Button>
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-gray-200/80 shadow-sm overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <div>
            <h3 className="font-semibold text-gray-900 text-sm flex items-center gap-2">
              <Shield className="w-4 h-4 text-blue-500" /> Audit Log
            </h3>
            <p className="text-xs text-gray-500 mt-0.5">{total} total events</p>
          </div>
          <Button variant="ghost" size="sm" onClick={() => refetch()} className="gap-1 text-xs">
            <RefreshCw className="w-3.5 h-3.5" /> Refresh
          </Button>
        </div>

        {isLoading ? (
          <div className="p-12 text-center text-gray-400">Loading audit logs...</div>
        ) : logs.length === 0 ? (
          <div className="p-12 text-center text-gray-400">
            <Shield className="w-8 h-8 mx-auto mb-2 text-gray-300" />
            <p>No audit events recorded yet</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {logs.map(log => (
              <div key={log.id} className="px-6 py-3.5 hover:bg-gray-50 transition-colors">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-violet-500 flex items-center justify-center text-white text-xs font-bold mt-0.5">
                      {log.userName?.[0] || <User className="w-3.5 h-3.5" />}
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-gray-900">{log.userName || "System"}</span>
                        <Badge className={`text-xs border-none ${ACTION_COLORS[log.action] || "bg-gray-100 text-gray-600"}`}>
                          {log.action}
                        </Badge>
                      </div>
                      <div className="text-xs text-gray-500 mt-0.5">
                        {log.resource}{log.resourceId ? ` #${log.resourceId}` : ""}
                        {log.userEmail ? ` · ${log.userEmail}` : ""}
                      </div>
                      {log.details && (
                        <div className="text-xs text-gray-400 mt-1 font-mono bg-gray-50 px-2 py-1 rounded">
                          {typeof log.details === "string" ? log.details : JSON.stringify(log.details).slice(0, 200)}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-1 text-xs text-gray-400 shrink-0">
                    <Clock className="w-3 h-3" />
                    {new Date(log.createdAt).toLocaleString()}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {totalPages > 1 && (
          <div className="px-6 py-3 border-t border-gray-200 flex items-center justify-between">
            <span className="text-xs text-gray-500">Page {page + 1} of {totalPages}</span>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" disabled={page === 0} onClick={() => setPage(p => p - 1)} className="gap-1 text-xs">
                <ChevronLeft className="w-3 h-3" /> Previous
              </Button>
              <Button variant="outline" size="sm" disabled={page >= totalPages - 1} onClick={() => setPage(p => p + 1)} className="gap-1 text-xs">
                Next <ChevronRight className="w-3 h-3" />
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
